import React from "react";
import { Plus, Trash2 } from "lucide-react";
import { Button } from "../../../../components/ui/button";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "../../../../components/ui/select";
import { Input } from "../../../../components/ui/input";
import { Checkbox } from "../../../../components/ui/checkbox";
import { Label } from "../../../../components/ui/label";
import {
	PatientFieldMapping,
	PATIENT_MATCH_FIELDS,
	PATIENT_PREFILL_FIELDS,
} from "../../types/ruleTypes";
import { FormSection } from "../../types/formTypes";
import { FIELD_REGISTRY } from "../../../job-configuration/field-registry";

interface PatientActionConfiguratorProps {
	ruleId: string;
	matchFields: Array<typeof PATIENT_MATCH_FIELDS[number]>;
	fieldMappings: PatientFieldMapping[];
	formSections: FormSection[];
	onPatientActionChange: (updates: {
		matchFields?: Array<typeof PATIENT_MATCH_FIELDS[number]>;
		fieldMappings?: PatientFieldMapping[];
	}) => void;
}

export const PatientActionConfigurator: React.FC<
	PatientActionConfiguratorProps
> = ({
	ruleId,
	matchFields,
	fieldMappings,
	formSections,
	onPatientActionChange,
}) => {
	// Get all form fields (excluding title and description)
	const allFormFields = formSections.flatMap((section) =>
		section.fields.filter(
			(f) => f.type !== "title" && f.type !== "description"
		)
	);

	const addMapping = () => {
		onPatientActionChange({
			fieldMappings: [
				...fieldMappings,
				{
					patientFieldKey: PATIENT_PREFILL_FIELDS[0],
					valueSource: "form_field",
					formFieldId: allFormFields[0]?.id,
				},
			],
		});
	};

	const removeMapping = (index: number) => {
		const removedMapping = fieldMappings[index];
		const newFieldMappings = fieldMappings.filter((_, i) => i !== index);

		// Also remove from matchFields if it was selected
		const needsMatchFieldUpdate = matchFields.includes(
			removedMapping.patientFieldKey as typeof PATIENT_MATCH_FIELDS[number]
		);

		if (needsMatchFieldUpdate) {
			const newMatchFields = matchFields.filter((f) => f !== removedMapping.patientFieldKey);
			onPatientActionChange({
				fieldMappings: newFieldMappings,
				matchFields: newMatchFields,
			});
		} else {
			onPatientActionChange({
				fieldMappings: newFieldMappings,
			});
		}
	};

	const updateMapping = (
		index: number,
		updates: Partial<PatientFieldMapping>
	) => {
		const newMappings = [...fieldMappings];
		newMappings[index] = { ...newMappings[index], ...updates };
		onPatientActionChange({ fieldMappings: newMappings });
	};

	const toggleMatchField = (
		fieldKey: typeof PATIENT_MATCH_FIELDS[number],
		checked: boolean
	) => {
		if (checked) {
			onPatientActionChange({ matchFields: [...matchFields, fieldKey] });
		} else {
			onPatientActionChange({ matchFields: matchFields.filter((f) => f !== fieldKey) });
		}
	};

	// Get already mapped patient fields to prevent duplicates
	const mappedPatientFields = new Set(fieldMappings.map((m) => m.patientFieldKey));

	// Get mapped fields that can be used for matching
	const availableMatchFields = Array.from(mappedPatientFields).filter(
		(field) => PATIENT_MATCH_FIELDS.includes(field as any)
	) as Array<typeof PATIENT_MATCH_FIELDS[number]>;

	return (
		<div className="space-y-3">
			{/* Field mappings section - FIRST */}
			<div>
				<div className="text-sm mb-2 mt-6 font-medium">Felder für Patientenerstellung/-zuordnung zuweisen:</div>
				<div className="space-y-2">
					{fieldMappings.map((mapping, index) => (
						<div key={`${ruleId}-mapping-${index}`} className="flex items-center gap-2">
							<div className="text-sm">Das Feld</div>
							{/* 1. Patient Field Select */}
							<Select
								value={mapping.patientFieldKey}
								onValueChange={(value) =>
									updateMapping(index, {
										patientFieldKey: value as typeof PATIENT_PREFILL_FIELDS[number],
									})
								}
							>
								<SelectTrigger className="w-[200px]">
									<SelectValue />
								</SelectTrigger>
								<SelectContent>
									{PATIENT_PREFILL_FIELDS.map((fieldKey) => (
										<SelectItem
											key={fieldKey}
											value={fieldKey}
											disabled={
												mappedPatientFields.has(fieldKey) &&
												mapping.patientFieldKey !== fieldKey
											}
										>
											{FIELD_REGISTRY[fieldKey]?.label || fieldKey.split(".")[1]}
										</SelectItem>
									))}
								</SelectContent>
							</Select>

							{/* 2. Value Source Select */}
							<Select
								value={mapping.valueSource}
								onValueChange={(value: "form_field" | "custom") =>
									updateMapping(index, {
										valueSource: value,
										...(value === "form_field"
											? { customValue: undefined }
											: { formFieldId: undefined }),
									})
								}
							>
								<SelectTrigger className="w-[240px]">
									<SelectValue />
								</SelectTrigger>
								<SelectContent>
									<SelectItem value="form_field">
										vorausfüllen mit dem Formularfeld
									</SelectItem>
									<SelectItem value="custom">
										vorausfüllen mit dem eigenen Wert
									</SelectItem>
								</SelectContent>
							</Select>

							{/* 3. Conditional Input */}
							{mapping.valueSource === "form_field" ? (
								<Select
									value={mapping.formFieldId}
									onValueChange={(value) =>
										updateMapping(index, { formFieldId: value })
									}
								>
									<SelectTrigger className="flex-1">
										<SelectValue placeholder="Feld auswählen" />
									</SelectTrigger>
									<SelectContent>
										{allFormFields.map((field) => (
											<SelectItem key={field.id} value={field.id}>
												{field.label}
											</SelectItem>
										))}
									</SelectContent>
								</Select>
							) : (
								<Input
									type={
										mapping.patientFieldKey.includes("date") ? "date" : "text"
									}
									value={mapping.customValue || ""}
									onChange={(e) =>
										updateMapping(index, {
											customValue: e.target.value,
										})
									}
									placeholder="Wert eingeben"
									className="flex-1"
								/>
							)}

							{/* 4. Delete Button */}
							<Button
								type="button"
								variant="ghost"
								size="sm"
								onClick={() => removeMapping(index)}
								className="h-8 w-8 p-0 text-red-600 hover:text-red-700"
							>
								<Trash2 className="h-4 w-4" />
							</Button>
						</div>
					))}
				</div>

				<Button
					type="button"
					variant="outline"
					size="sm"
					onClick={addMapping}
					className="gap-2 mt-2"
					disabled={fieldMappings.length >= PATIENT_PREFILL_FIELDS.length}
				>
					<Plus className="h-4 w-4" />
					Feldzuweisung hinzufügen
				</Button>
			</div>

			{/* Matching criteria section - SECOND (only shows mapped fields) */}
			<div>
				<div className="text-sm mb-2 font-medium mt-6">
					Wenn folgende Felder mit einem existierenden Patienten übereinstimmen, diesen Patienten auswählen:
				</div>
				{availableMatchFields.length > 0 ? (
					<div className="flex flex-wrap gap-4">
						{availableMatchFields.map((fieldKey) => (
							<div key={fieldKey} className="flex items-center space-x-2">
								<Checkbox
									id={`match-${ruleId}-${fieldKey}`}
									checked={matchFields.includes(fieldKey)}
									onCheckedChange={(checked) =>
										toggleMatchField(fieldKey, checked as boolean)
									}
								/>
								<Label
									htmlFor={`match-${ruleId}-${fieldKey}`}
									className="text-sm font-normal cursor-pointer"
								>
									{FIELD_REGISTRY[fieldKey]?.label || fieldKey.split(".")[1]}
								</Label>
							</div>
						))}
						{matchFields.length === 0 ? (
							<div className="text-sm text-red-500 italic">
								Ohne Auswahl wird der Patient immer neu erstellt.
							</div>
						):(
							<div className="text-sm text-gray-500 italic">
								Sollten die Felder nicht übereinstimmen, wird der Patient neu erstellt.
							</div>
						)}
					</div>
				) : (
					<div className="text-sm text-gray-500 italic">
						Erstellen Sie zuerst Feldzuweisungen, um Auswahlkriterien festzulegen.
					</div>
				)}
			</div>
		</div>
	);
};
